import React, { useState, useEffect } from "react";
import { NavLink, useNavigate, Link, useLocation } from "react-router-dom";
import { Search, Heart, LayoutDashboard, Sparkles, ChevronRight, LogIn, LogOut, Sliders, Bot, MessageSquare, PenLine, Code, Microscope, Zap, DollarSign, GraduationCap, Film, Gem, Crown, Menu, X, Palette, Wallet, Target, HelpCircle, FileText, Briefcase, Megaphone, BarChart3, Command as CmdIcon, Wand2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { CommandPalette } from "@/components/CommandPalette";
import { HelperWidget } from "@/components/HelperWidget";
import { GlobalHeader } from "@/components/GlobalHeader";
import { Footer } from "@/components/Footer";

const NAV = [
  { to: "/agents", label: "AI Agents", icon: Bot, tint: "text-teal-600" },
  { to: "/?cat=Chat+%26+LLMs", label: "Chat & LLMs", icon: MessageSquare, tint: "text-sky-600" },
  { to: "/?cat=Image", label: "Image", icon: Palette, tint: "text-rose-500" },
  { to: "/?cat=Video", label: "Video", icon: Film, tint: "text-sky-600" },
  { to: "/?cat=Music+%26+Audio", label: "Music & Audio", icon: Sparkles, tint: "text-teal-600" },
  { to: "/?cat=Voice", label: "Voice", icon: Microscope, tint: "text-rose-500" },
  { to: "/?cat=Writing+%26+Copy", label: "Writing", icon: PenLine, tint: "text-sky-600" },
  { to: "/?cat=No-Code+%26+Vibe+Coding", label: "No-Code & Vibe Coding", icon: Code, tint: "text-teal-600" },
  { to: "/?cat=Automation+%26+Workflows", label: "Automation & Workflows", icon: Zap, tint: "text-rose-500" },
  { to: "/?cat=Research", label: "Research", icon: Microscope, tint: "text-sky-600" },
  { to: "/?cat=Productivity", label: "Productivity", icon: Briefcase, tint: "text-teal-600" },
  { to: "/?cat=Health+%26+Wellness", label: "Health", icon: Heart, tint: "text-rose-500" },
];

const ALL_ACCESS = [
  { to: "/prompts", label: "Prompt Library", icon: PenLine, tid: "nav-prompts" },
  { to: "/chat", label: "Chat Arena", icon: MessageSquare, tid: "nav-chat-arena" },
  { to: "/accountant", label: "AI Accountant", icon: Wallet, tid: "nav-accountant" },
  { to: "/life", label: "Life Hub", icon: Target, tid: "nav-life" },
  { to: "/trends", label: "Visual Trends", icon: Palette, tid: "nav-trends" },
  { to: "/humanize", label: "Humanize", icon: Sparkles, tid: "nav-humanize" },
];

function AllAccessPanel() {
  return (
    <div className="curio-card p-4" data-testid="all-access-panel">
      <div className="flex items-center gap-2 mb-3 px-1">
        <Crown size={13} className="text-[var(--curio-coral)]" />
        <span className="text-[10px] tracking-[0.24em] font-bold text-[#5B6478] uppercase">All Access</span>
      </div>
      <div className="grid grid-cols-2 gap-1.5">
        {ALL_ACCESS.map(item => {
          const Icon = item.icon;
          return (
            <NavLink
              key={item.to}
              to={item.to}
              className="flex items-center gap-2 rounded-xl px-2.5 py-2 text-xs font-medium text-[#0B1220] bg-white/45 border border-white/60 hover:bg-white/85 hover:shadow-sm transition-colors"
              data-testid={item.tid}
            >
              <Icon size={13} className="text-teal-600 flex-shrink-0" /> {item.label}
            </NavLink>
          );
        })}
      </div>
    </div>
  );
}

export function Layout({ children, hideRightPanel = false, rightPanel }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const loc = useLocation();
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);

  useEffect(() => { setOpen(false); }, [loc.pathname]);

  const goSearch = (e) => {
    e.preventDefault();
    if (q.trim()) navigate(`/?search=${encodeURIComponent(q.trim())}`);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-white text-[#0B1220]">
      {/* Pure white canvas — 2033 minimalist */}
      <div className="fixed inset-0 -z-10 bg-white pointer-events-none" aria-hidden="true" data-testid="wikifi-background" />

      {/* Global unified sticky header — same as homepage */}
      <GlobalHeader />

      <div className="max-w-[1920px] mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-12 gap-4 lg:gap-6 pt-6 sm:pt-8 pb-16">
        {/* Center */}
        <main className={`col-span-12 ${hideRightPanel ? 'lg:col-span-12' : 'lg:col-span-9'} min-w-0`}>
          {children}
        </main>

        {/* Right contextual */}
        {!hideRightPanel && (
          <aside className="hidden lg:block lg:col-span-3">
            <div className="sticky top-20 space-y-4 max-h-[calc(100vh-6rem)] overflow-y-auto scrollbar-hide pb-4">
              {rightPanel}
            </div>
          </aside>
        )}
      </div>

      <Footer/>

      {/* Global ambient layers */}
      <CommandPalette />
      <HelperWidget />
    </div>
  );
}

const Plus2 = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="text-rose-500"><path d="M12 5v14M5 12h14"/></svg>
);
